import type { PlanNode } from '@timelog/pm-shared';
import { useTranslation } from 'react-i18next';

import type { EditField, NodeFieldsPatch } from '../editor';
import { createPlanNode, usePlanEditor } from '../editor';

import { editFieldOrder } from './editFields';
import type { CommitIntent } from './InlineTextCell';

import { parseDateInput } from '@/lib/format';

/**
 * Shared commit pipeline for every inline cell: parse the buffer into a
 * field patch, then act on the commit intent — stop, Tab to the next field
 * (title → hours → start → target), or the ⌘Return outline family
 * (new sibling / sub-task / one level up, BR-2-clamped).
 */

/** Parses a hours commit; null = ignore (unparsable), undefined = clear. */
const parseHoursInput = (raw: string): number | undefined | null => {
  const text = raw.trim().replace(',', '.');
  if (text === '') return undefined;
  const value = Number(text);
  if (!Number.isFinite(value) || value < 0) return null;
  return Math.round(value * 100) / 100;
};

/** `untitled` is the name an emptied title falls back to — stored, in the UI language. */
const fieldPatch = (field: EditField, value: string, untitled: string): NodeFieldsPatch | null => {
  switch (field) {
    case 'title':
      return { name: value.trim() === '' ? untitled : value.trim() };
    case 'hours': {
      const hours = parseHoursInput(value);
      return hours === null ? null : { estimatedHours: hours };
    }
    case 'target': {
      const iso = parseDateInput(value);
      return iso === null ? null : { targetDate: iso };
    }
    // The schedule (BR-16). Typed here rather than dragged, because the drag
    // needs a track wide enough to reach the date — which a small screen does
    // not have. Ordering is NOT enforced: `planDateWarnings` reports it and the
    // PM's number is kept, exactly as a typed hours figure is.
    case 'start': {
      const iso = parseDateInput(value);
      return iso === null ? null : { startDate: iso };
    }
    case 'end': {
      const iso = parseDateInput(value);
      return iso === null ? null : { endDate: iso };
    }
  }
};

export const useFieldEditing = () => {
  const { t } = useTranslation(['pmPlanning', 'projectManagement']);
  const { state, dispatch, flat } = usePlanEditor();
  // Stored as the new row's name, so it is written in the UI language.
  const newTaskName = t('projectManagement:projectPlan.NewTaskName');

  /** Position right after `afterId` inside `containerId`'s child list. */
  const positionAfter = (containerId: string | null, afterId: string) => {
    const siblings =
      containerId === null
        ? state.document.plan
        : (flat.index.nodeById.get(containerId)?.children ?? []);
    const index = siblings.findIndex((sibling) => sibling.id === afterId);
    return { parentId: containerId, childIndex: index + 1 };
  };

  const insertRelative = (node: PlanNode, intent: CommitIntent) => {
    const parentId = flat.index.parentById.get(node.id) ?? null;

    if (intent === 'new-child') {
      dispatch({
        type: 'INSERT_NODE',
        node: createPlanNode('task', newTaskName),
        position: { parentId: node.id, childIndex: Number.MAX_SAFE_INTEGER },
        asChildOf: node.id,
        focusTitle: true,
      });
      return;
    }

    const newSibling = () => {
      // A root row is stored as kind 'deliverable' (BR-2) — a top-level task
      // in the UI, which has no Deliverable concept.
      const kind = parentId === null ? 'deliverable' : 'task';
      dispatch({
        type: 'INSERT_NODE',
        node: createPlanNode(kind, newTaskName),
        position: positionAfter(parentId, node.id),
        focusTitle: true,
      });
    };

    if (intent === 'new-sibling' || parentId === null) {
      newSibling();
      return;
    }

    // new-parent-sibling: one level up. From depth 1 that would be a root
    // task (BR-2) — clamp to a plain sibling instead.
    const grandParentId = flat.index.parentById.get(parentId) ?? null;
    if (grandParentId === null) {
      newSibling();
      return;
    }
    dispatch({
      type: 'INSERT_NODE',
      node: createPlanNode('task', newTaskName),
      position: positionAfter(grandParentId, parentId),
      focusTitle: true,
    });
  };

  const commitField = (node: PlanNode, field: EditField, value: string, intent: CommitIntent) => {
    const patch = fieldPatch(field, value, t('cells.Untitled'));
    // A cell commits on Tab and Enter even when nothing was typed. Writing that
    // back would bump the revision and queue a save that changes nothing.
    const changes =
      patch !== null &&
      Object.entries(patch).some(([key, next]) => node[key as keyof PlanNode] !== next);
    if (patch && changes) dispatch({ type: 'SET_NODE_FIELDS', nodeId: node.id, patch });

    switch (intent) {
      case 'commit':
        dispatch({ type: 'STOP_EDITING' });
        return;
      case 'tab-next':
      case 'tab-prev': {
        // A panel date field walks its own pair, never into a row cell that is
        // not on screen beside it.
        const order = editFieldOrder(node);
        const position = order.indexOf(field);
        const next = order[position + (intent === 'tab-next' ? 1 : -1)];
        if (next) {
          dispatch({ type: 'START_EDITING', nodeId: node.id, field: next });
        } else {
          dispatch({ type: 'STOP_EDITING' });
        }
        return;
      }
      default:
        insertRelative(node, intent);
    }
  };

  const isEditing = (nodeId: string, field: EditField): boolean =>
    state.editing?.nodeId === nodeId && state.editing.field === field;

  /**
   * `openPicker` is the pointer gesture: a click on a date cell opens its
   * calendar with the field, where a Tab into the same cell leaves it closed
   * (see `InlineDateCell`).
   */
  const startEditing = (nodeId: string, field: EditField, openPicker = false) =>
    dispatch({ type: 'START_EDITING', nodeId, field, openPicker });

  /** True when this cell's editor should open its panel on mount. */
  const shouldOpenPicker = (nodeId: string, field: EditField): boolean =>
    state.editing?.nodeId === nodeId &&
    state.editing.field === field &&
    state.editing.openPicker === true;

  const stopEditing = () => dispatch({ type: 'STOP_EDITING' });

  return { commitField, isEditing, shouldOpenPicker, startEditing, stopEditing };
};
